<template>
    <nav
        :class="[
            'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
            scrolled
                ? 'bg-[#17364a] border-b border-[#1e4d63]'
                : 'bg-white border-b border-[#E5E5E5]'
        ]"
    >
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div class="flex h-16 items-center justify-between gap-8">

                <!-- Logo -->
                <a href="/" class="flex shrink-0 items-center">
                    <img
                        :class="['h-10 w-auto object-contain transition-all duration-300', scrolled ? 'brightness-0 invert' : '']"
                        src="/images/logo.png"
                        alt="Nobel Academy"
                    />
                </a>

                <!-- Desktop links -->
                <div class="hidden lg:flex lg:items-center lg:gap-1 flex-1">
                    <template v-for="item in navigation" :key="item.name">
                        <!-- Plain link -->
                        <router-link
                            v-if="!item.dropdown"
                            :to="item.href"
                            :class="[
                                'relative px-3 py-2 text-sm font-medium transition-colors duration-200 group',
                                scrolled ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-[#17364a]'
                            ]"
                        >
                            {{ item.name }}
                            <span class="absolute bottom-0 left-3 right-3 h-0.5 bg-[#A07830] scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left"></span>
                        </router-link>

                        <!-- Dropdown -->
                        <Menu v-else as="div" class="relative">
                            <MenuButton
                                :class="[
                                    'flex items-center gap-1 px-3 py-2 text-sm font-medium transition-colors duration-200',
                                    scrolled ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-[#17364a]'
                                ]"
                            >
                                {{ item.name }}
                                <ChevronDownIcon class="h-4 w-4" aria-hidden="true" />
                            </MenuButton>
                            <Transition
                                enter="transition ease-out duration-100"
                                enter-from="opacity-0 translate-y-1"
                                enter-to="opacity-100 translate-y-0"
                                leave="transition ease-in duration-75"
                                leave-from="opacity-100 translate-y-0"
                                leave-to="opacity-0 translate-y-1"
                            >
                                <MenuItems class="absolute left-0 mt-1 w-52 bg-white border border-[#E5E5E5] shadow-lg focus:outline-none z-50">
                                    <MenuItem v-slot="{ active }" v-for="subItem in item.dropdown.items" :key="subItem.name">
                                        <router-link
                                            :to="subItem.href"
                                            :class="[
                                                active ? 'bg-[#F5F5F5] text-[#17364a]' : 'text-gray-700',
                                                'block px-4 py-2.5 text-sm border-b border-[#F5F5F5] last:border-0'
                                            ]"
                                        >
                                            {{ subItem.name }}
                                        </router-link>
                                    </MenuItem>
                                </MenuItems>
                            </Transition>
                        </Menu>
                    </template>
                </div>

                <!-- Right: user or auth buttons -->
                <div class="flex items-center gap-3">
                    <!-- Authenticated: avatar dropdown -->
                    <Menu v-if="user" as="div" class="relative">
                        <MenuButton class="flex items-center gap-2 focus:outline-none">
                            <img
                                class="h-8 w-8 rounded-full object-cover ring-2 ring-[#A07830]"
                                :src="user.avatar || '/images/default-avatar.png'"
                                alt="User Avatar"
                            />
                            <ChevronDownIcon
                                :class="['h-4 w-4 transition-colors', scrolled ? 'text-gray-300' : 'text-gray-600']"
                                aria-hidden="true"
                            />
                        </MenuButton>
                        <Transition
                            enter="transition ease-out duration-100"
                            enter-from="opacity-0 translate-y-1"
                            enter-to="opacity-100 translate-y-0"
                            leave="transition ease-in duration-75"
                            leave-from="opacity-100 translate-y-0"
                            leave-to="opacity-0 translate-y-1"
                        >
                            <MenuItems class="absolute right-0 mt-1 w-52 bg-white border border-[#E5E5E5] shadow-lg focus:outline-none z-50">
                                <MenuItem v-slot="{ active }" v-for="item in userDropdownItems" :key="item.name">
                                    <a
                                        :href="item.href"
                                        :class="[
                                            active ? 'bg-[#F5F5F5] text-[#17364a]' : 'text-gray-700',
                                            'flex items-center gap-2 px-4 py-2.5 text-sm border-b border-[#F5F5F5] last:border-0'
                                        ]"
                                    >
                                        <component :is="item.icon" class="h-4 w-4 text-[#A07830]" />
                                        {{ item.name }}
                                    </a>
                                </MenuItem>
                            </MenuItems>
                        </Transition>
                    </Menu>

                    <!-- Guest: auth buttons -->
                    <template v-else>
                        <router-link
                            to="/login"
                            :class="[
                                'hidden sm:inline-flex items-center px-4 py-2 text-sm font-semibold border transition-colors duration-200',
                                scrolled
                                    ? 'border-[#A07830] text-[#C9A55A] hover:bg-[#A07830] hover:text-white'
                                    : 'border-[#A07830] text-[#A07830] hover:bg-[#A07830] hover:text-white'
                            ]"
                        >
                            Login
                        </router-link>
                        <router-link
                            to="/register"
                            class="hidden sm:inline-flex items-center px-4 py-2 text-sm font-semibold bg-[#17364a] text-white hover:bg-[#A07830] transition-colors duration-200"
                        >
                            Get Started
                        </router-link>
                    </template>

                    <!-- Mobile hamburger -->
                    <button
                        class="lg:hidden p-2 focus:outline-none"
                        :class="scrolled ? 'text-gray-300' : 'text-gray-700'"
                        @click="mobileOpen = !mobileOpen"
                        aria-label="Toggle menu"
                    >
                        <Bars3Icon v-if="!mobileOpen" class="h-6 w-6" />
                        <XMarkIcon v-else class="h-6 w-6" />
                    </button>
                </div>
            </div>
        </div>

        <!-- Mobile drawer -->
        <Transition
            enter-active-class="transition ease-out duration-300"
            enter-from-class="opacity-0 -translate-y-4"
            enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition ease-in duration-200"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 -translate-y-4"
        >
            <div v-if="mobileOpen" class="lg:hidden fixed inset-0 top-16 bg-[#17364a] z-40 overflow-y-auto">
                <div class="px-6 py-8 flex flex-col gap-1">
                    <template v-for="item in navigation" :key="item.name">
                        <template v-if="!item.dropdown">
                            <router-link
                                :to="item.href"
                                class="block py-3 text-lg font-medium text-gray-300 hover:text-white border-b border-[#1e4d63]"
                                @click="mobileOpen = false"
                            >
                                {{ item.name }}
                            </router-link>
                        </template>
                        <template v-else>
                            <div class="border-b border-[#1e4d63]">
                                <button
                                    class="flex w-full items-center justify-between py-3 text-lg font-medium text-gray-300 hover:text-white"
                                    @click="mobileDropdownOpen = !mobileDropdownOpen"
                                >
                                    {{ item.name }}
                                    <ChevronDownIcon :class="['h-5 w-5 transition-transform', mobileDropdownOpen ? 'rotate-180' : '']" />
                                </button>
                                <div v-if="mobileDropdownOpen" class="pl-4 pb-2 flex flex-col gap-1">
                                    <router-link
                                        v-for="sub in item.dropdown.items"
                                        :key="sub.name"
                                        :to="sub.href"
                                        class="block py-2 text-sm text-gray-400 hover:text-[#C9A55A]"
                                        @click="mobileOpen = false"
                                    >
                                        {{ sub.name }}
                                    </router-link>
                                </div>
                            </div>
                        </template>
                    </template>

                    <div class="mt-6 flex flex-col gap-3">
                        <router-link
                            v-if="!user"
                            to="/login"
                            class="w-full py-3 text-center text-sm font-semibold border border-[#A07830] text-[#C9A55A]"
                            @click="mobileOpen = false"
                        >
                            Login
                        </router-link>
                        <router-link
                            v-if="!user"
                            to="/register"
                            class="w-full py-3 text-center text-sm font-semibold bg-[#A07830] text-white"
                            @click="mobileOpen = false"
                        >
                            Get Started
                        </router-link>
                        <template v-if="user">
                            <a v-for="item in userDropdownItems" :key="item.name" :href="item.href"
                                class="flex items-center gap-2 py-3 text-sm text-gray-400 hover:text-white border-b border-[#1e4d63]"
                                @click="mobileOpen = false"
                            >
                                <component :is="item.icon" class="h-4 w-4 text-[#A07830]" />
                                {{ item.name }}
                            </a>
                        </template>
                    </div>
                </div>
            </div>
        </Transition>
    </nav>

    <!-- Spacer so content isn't hidden under fixed nav -->
    <div class="h-16"></div>
</template>

<script setup>
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import {
    Bars3Icon,
    XMarkIcon,
    AcademicCapIcon,
    UserIcon,
    ArrowLeftEndOnRectangleIcon
} from '@heroicons/vue/24/outline';
import { ChevronDownIcon } from '@heroicons/vue/20/solid';
import { useAuth } from '@/composables/useAuth';
import { onMounted, onUnmounted, ref } from 'vue';
import { getFeaturedCategories } from '@/services/categoryService';

const { getUser } = useAuth();
const user = ref(null);
const categories = ref([]);
const scrolled = ref(false);
const mobileOpen = ref(false);
const mobileDropdownOpen = ref(false);

const navigation = [
    {
        name: 'Programs', href: '#', current: false, dropdown: {
            items: []
        }
    },
    { name: 'Books', href: '/books', current: false, dropdown: false },
    { name: 'About Us', href: '/about-us', current: false, dropdown: false },
    { name: 'Testimonial', href: '/#testimonials', current: false, dropdown: false },
    { name: 'Contact Us', href: '/contact-us', current: false, dropdown: false },
    { name: 'Privacy Policy', href: '/privacy-policy', current: false, dropdown: false },
];

const userDropdownItems = [
    { name: 'Account', href: '/account', icon: UserIcon },
    { name: 'Learning', href: '/learning', icon: AcademicCapIcon },
    { name: 'Logout', href: '/logout', icon: ArrowLeftEndOnRectangleIcon }
];

const handleScroll = () => {
    scrolled.value = window.scrollY > 20;
};

onMounted(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });

    getUser()
        .then(userData => { user.value = userData; })
        .catch(() => {});

    getFeaturedCategories().then(data => {
        categories.value = data.data;
        navigation[0].dropdown.items = categories.value.map(category => ({
            name: category.name,
            href: `/courses?category=${category.slug}`
        }));
    });
});

onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll);
});
</script>